import {
  ClockCircleOutlined,
  ReloadOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { Button, Drawer, Input, Table, Tooltip } from 'antd'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useHistory, useLocation } from 'react-router-dom'
import styled from 'styled-components/macro'
import { BodyText, H3 } from 'src/branding/typography'
import {
  FoodSafetyHeaderRow,
  FoodSafetyHeroBand,
  FoodSafetyPageTitle,
} from 'src/components/food-safety/FoodSafetyPageChrome'
import {
  FOOD_SAFETY_SECONDARY_TEXT,
  FOOD_SAFETY_SURFACE,
} from 'src/constants/foodSafety'
import FadeIn from 'src/components/styled/FadeIn'
import Loading from 'src/components/styled/Loading'
import PermissionBlockScreen from 'src/components/shared/PermissionBlockScreen'
import ApprovalDetail from 'src/screens/approvals/ApprovalDetail'
import useIsBlocked from 'src/hooks/useIsBlocked'
import { useWindowWidth, windowSize } from 'src/utils/useWindowWidth'
import {
  fetchPendingApprovals,
  fetchApprovalSetup,
  filterApprovalItems,
  formatApprovalAge,
  formatApprovalTimestamp,
} from 'src/utils/dashboardApprovalsApi'
import { notifyApprovalsChanged } from 'src/hooks/usePendingApprovalsCount'
import { venueHomeReturnPath } from 'src/utils/venueHomeApi'

const Wrapper = styled(FadeIn)`
  width: 100%;
  max-width: 72rem;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.sm};
  box-sizing: border-box;

  ${({ theme }) => theme.device('md')`
    padding: ${theme.spacing.md};
  `};
`

const HeroIntro = styled.p`
  margin: ${({ theme }) => theme.spacing.sm} 0 0;
  max-width: 38rem;
  font-size: 0.95rem;
  line-height: 1.45;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const CountLine = styled(BodyText)`
  margin: 0.2rem 0 0;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const ErrorLine = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colours.chilliRed || '#cf1322'};
  font-size: 0.9rem;
`

const EmptyCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin: 0 0 ${({ theme }) => theme.spacing.md};
  padding: 0.85rem 1rem;
  border: 1px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};
  border-radius: ${FOOD_SAFETY_SURFACE.CARD_RADIUS};
  background: #fff;
  box-shadow: ${FOOD_SAFETY_SURFACE.CARD_SHADOW};
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  font-size: 0.95rem;
`

const ListCard = styled.div`
  margin: 0 0 ${({ theme }) => theme.spacing.md};
  background: #fff;
  border: 1px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};
  border-radius: ${FOOD_SAFETY_SURFACE.CARD_RADIUS};
  box-shadow: ${FOOD_SAFETY_SURFACE.CARD_SHADOW};
  overflow: hidden;

  .ant-table-row {
    cursor: pointer;
  }

  .ant-table-thead > tr > th {
    word-break: keep-all;
    overflow-wrap: normal;
    hyphens: none;
    white-space: normal;
  }
`

const GuideCard = styled.section`
  margin: 0;
  padding: 1rem 1.1rem 1.15rem;
  border: 1px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};
  border-radius: ${FOOD_SAFETY_SURFACE.CARD_RADIUS};
  background: #fff;
  box-shadow: ${FOOD_SAFETY_SURFACE.CARD_SHADOW};
`

const GuideTitle = styled.h2`
  margin: 0 0 0.45rem;
  font-size: 1.05rem;
  line-height: 1.3;
  font-weight: 700;
  color: ${({ theme }) => theme.colours.bbqBlack || '#000'};
`

const GuideBody = styled.p`
  margin: 0 0 0.65rem;
  max-width: 40rem;
  font-size: 0.9rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colours.bbqBlack || '#262626'};
`

const SuppliersLink = styled(Link)`
  display: inline-block;
  padding: 0.45rem 0.9rem;
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  background: ${({ theme }) => theme.colours.lettuceGreen || '#b5d922'};
  color: #fff;
  font-weight: 600;
  font-size: 0.9rem;
  line-height: 1.3;
  text-decoration: none;

  &:hover {
    color: #fff;
    filter: brightness(0.96);
  }
`

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin: 0 0 ${({ theme }) => theme.spacing.md};
`

const ApprovalCard = styled.button`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  width: 100%;
  text-align: left;
  background: ${({ theme }) => theme.colours.mayoWhite};
  border: 1px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};
  border-radius: ${FOOD_SAFETY_SURFACE.CARD_RADIUS};
  box-shadow: ${FOOD_SAFETY_SURFACE.CARD_SHADOW};
  padding: ${({ theme }) => theme.spacing.md};
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colours.lettuceGreen};
  }
`

const CardTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing.sm};
`

const CardMeta = styled(BodyText)`
  margin: 0;
  color: #555;
`

const ExpiredTag = styled.span`
  color: ${({ theme }) => theme.colours.chilliRed};
  font-weight: 700;
  white-space: nowrap;
`

const ValueText = styled.span`
  font-weight: 700;
`

/**
 * Orders Awaiting Approval — the in-dashboard approval area for authorised
 * approvers. Shows every pending restricted-supplier approval batch for the
 * venue; decisions reuse the same workflow as the emailed approval links.
 */
const approvalTokenFromSearch = search => {
  const token = new URLSearchParams(
    String(search || '').replace(/^\?/, ''),
  ).get('token')
  const text = String(token || '').trim()
  return text || null
}

const Approvals = () => {
  const venue = useSelector(state => state.venue.venue)
  const { _id: venueId, title: venueTitle } = venue || {}
  const location = useLocation()
  const history = useHistory()
  const linkedToken = useMemo(
    () => approvalTokenFromSearch(location.search),
    [location.search],
  )
  const isBlocked = useIsBlocked('approvals')

  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState([])
  const [error, setError] = useState(null)
  const [openToken, setOpenToken] = useState(linkedToken)
  const [setup, setSetup] = useState(null)
  const [setupError, setSetupError] = useState(null)
  const [query, setQuery] = useState('')
  const [lastUpdated, setLastUpdated] = useState(null)
  const [now, setNow] = useState(Date.now())
  const requestId = useRef(0)
  const visibleItems = useMemo(
    () => filterApprovalItems(items, query),
    [items, query],
  )

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    if (linkedToken) setOpenToken(linkedToken)
  }, [linkedToken])

  const leaveFinishedHomeTask = useCallback(() => {
    const back = venueHomeReturnPath(venueId, location.search)
    if (back) history.replace(back)
  }, [venueId, location.search, history])

  const width = useWindowWidth()
  const isMobile = ['xs', 'sm'].includes(windowSize(width))

  const loadApprovals = useCallback(async () => {
    if (!venueId || isBlocked) return
    const id = ++requestId.current
    setLoading(true)
    setError(null)
    setSetupError(null)
    const [listResult, setupResult] = await Promise.allSettled([
      fetchPendingApprovals({ venueId }),
      fetchApprovalSetup({ venueId }),
    ])
    if (id !== requestId.current) return
    if (listResult.status === 'fulfilled') {
      setItems(listResult.value.items)
      setLastUpdated(Date.now())
      notifyApprovalsChanged()
    } else {
      setError(
        'Could not refresh orders. Please try again. Any orders shown are from the last successful update.',
      )
    }
    if (setupResult.status === 'fulfilled') setSetup(setupResult.value)
    else
      setSetupError(
        'Could not check approval setup. Please refresh to try again.',
      )
    setLoading(false)
  }, [venueId, isBlocked])

  useEffect(() => {
    setItems([])
    setSetup(null)
    setQuery('')
    setLastUpdated(null)
    setOpenToken(linkedToken)
    loadApprovals()
    return () => {
      requestId.current += 1
    }
    // Token changes are handled separately without clearing the list or filters.
  }, [loadApprovals])

  const handleDecided = useCallback(() => {
    requestId.current += 1
    setItems(current => current.filter(item => item.token !== openToken))
    setOpenToken(null)
    notifyApprovalsChanged()
    loadApprovals()
  }, [openToken, loadApprovals])

  const tableColumns = useMemo(
    () => [
      {
        title: 'Supplier',
        dataIndex: 'supplierTitle',
        key: 'supplierTitle',
        render: (title, record) => (
          <span>
            {title || '—'}
            {record.isExpired && (
              <Tooltip title='Expired — will be automatically rejected'>
                <WarningOutlined style={{ color: '#cf1322', marginLeft: 8 }} />
              </Tooltip>
            )}
          </span>
        ),
      },
      {
        title: 'PO number',
        dataIndex: 'purchaseOrderNumber',
        key: 'purchaseOrderNumber',
        responsive: ['sm'],
        render: po => po || '—',
      },
      {
        title: 'Requested by',
        dataIndex: 'requestedBy',
        key: 'requestedBy',
        render: requestedBy => requestedBy || '—',
      },
      {
        title: 'Items',
        dataIndex: 'itemCount',
        key: 'itemCount',
        align: 'center',
        width: 80,
      },
      {
        title: 'Value (inc VAT)',
        dataIndex: 'total',
        key: 'total',
        align: 'right',
        render: total => `£${Number(total || 0).toFixed(2)}`,
      },
      {
        title: 'Submitted',
        dataIndex: 'submittedAt',
        key: 'submittedAt',
        render: submittedAt => (
          <span>
            {formatApprovalTimestamp(submittedAt)}
            <br />
            <span
              style={{
                color:
                  now - new Date(submittedAt).getTime() >= 86400000
                    ? '#ad6800'
                    : undefined,
              }}
            >
              {formatApprovalAge(submittedAt, now)}
            </span>
          </span>
        ),
      },
      {
        title: '',
        key: 'review',
        align: 'right',
        render: (_, record) => (
          <Button
            type='link'
            onClick={event => {
              event.stopPropagation()
              setOpenToken(record.token)
            }}
          >
            Review
          </Button>
        ),
      },
    ],
    [now],
  )

  if (isBlocked) {
    return <PermissionBlockScreen />
  }

  if (!venueId || (loading && !lastUpdated && !error)) {
    return <Loading />
  }

  const openBatch = items.find(item => item.token === openToken)

  const waitingLabel = `${items.filter(item => !item.isExpired).length} order${
    items.filter(item => !item.isExpired).length === 1 ? '' : 's'
  } waiting`

  return (
    <Wrapper>
      <FoodSafetyHeroBand>
        <FoodSafetyHeaderRow>
          <div>
            <FoodSafetyPageTitle>Orders awaiting approval</FoodSafetyPageTitle>
            <CountLine>
              {venueTitle ? `${venueTitle} · ${waitingLabel}` : waitingLabel}
            </CountLine>
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadApprovals}
            loading={loading}
          >
            Refresh
          </Button>
        </FoodSafetyHeaderRow>
        <HeroIntro>
          Orders to a restricted supplier wait here until they are signed off.
          They are not sent to the supplier until then.
        </HeroIntro>
      </FoodSafetyHeroBand>

      {lastUpdated && (
        <CountLine role='status'>
          Last updated: {formatApprovalTimestamp(lastUpdated)}
        </CountLine>
      )}
      {error && <ErrorLine role='alert'>{error}</ErrorLine>}
      {setupError && <ErrorLine role='alert'>{setupError}</ErrorLine>}
      {items.length > 0 && (
        <Input.Search
          aria-label='Search by supplier, requester or PO number'
          placeholder='Search supplier, requester or PO number'
          value={query}
          onChange={event => setQuery(event.target.value)}
          style={{ margin: '1rem 0' }}
        />
      )}
      {items.length === 0 ? (
        !error &&
        setup &&
        !setupError &&
        (setup.configured ? (
          <EmptyCard>
            <ClockCircleOutlined />
            <span>
              <strong>You’re all caught up</strong>
              <br />
              There are currently no orders awaiting approval.
            </span>
          </EmptyCard>
        ) : (
          <GuideCard>
            <GuideTitle>Approvals are not configured</GuideTitle>
            <GuideBody>
              Use order approvals to make sure selected orders are reviewed
              before they are sent to suppliers. Nominated approvers can approve
              or reject them.
            </GuideBody>
            {setup.canConfigure ? (
              <>
                <GuideBody>
                  Choose who can approve orders and when approval is required.
                  In Your suppliers, edit a supplier, enable Restricted supplier
                  and add approver email addresses. Give dashboard approvers an
                  appropriate user role with permission to approve orders.
                </GuideBody>
                <SuppliersLink to={`/v/${venueId}/suppliers`}>
                  Set up order approvals
                </SuppliersLink>
              </>
            ) : (
              <GuideBody>
                Please ask an administrator to set up approvers and approval
                rules for this venue.
              </GuideBody>
            )}
          </GuideCard>
        ))
      ) : visibleItems.length === 0 ? (
        <EmptyCard>No orders match your search.</EmptyCard>
      ) : isMobile ? (
        <CardList>
          {visibleItems.map(item => (
            <ApprovalCard
              key={item.token}
              type='button'
              onClick={() => setOpenToken(item.token)}
              data-testid={`approval-card-${item.token}`}
            >
              <CardTitleRow>
                <H3 style={{ margin: 0 }}>{item.supplierTitle || 'Order'}</H3>
                <ValueText>£{Number(item.total || 0).toFixed(2)}</ValueText>
              </CardTitleRow>
              {item.purchaseOrderNumber && (
                <CardMeta>PO {item.purchaseOrderNumber}</CardMeta>
              )}
              {item.requestedBy && <CardMeta>By {item.requestedBy}</CardMeta>}
              <CardMeta>
                {item.itemCount} item{item.itemCount === 1 ? '' : 's'} ·{' '}
                {formatApprovalTimestamp(item.submittedAt)} ·{' '}
                {formatApprovalAge(item.submittedAt, now)}
              </CardMeta>
              {item.isExpired && <ExpiredTag>Expired</ExpiredTag>}
              <span>Review</span>
            </ApprovalCard>
          ))}
        </CardList>
      ) : (
        <ListCard>
          <Table
            columns={tableColumns}
            dataSource={visibleItems.map(item => ({
              ...item,
              key: item.token,
            }))}
            pagination={false}
            scroll={{ x: 850 }}
            onRow={record => ({ onClick: () => setOpenToken(record.token) })}
          />
        </ListCard>
      )}

      <Drawer
        title={
          openBatch
            ? `Review approval · ${openBatch.supplierTitle || 'Order'}`
            : 'Review approval'
        }
        visible={Boolean(openToken)}
        onClose={() => setOpenToken(null)}
        width={Math.min(720, typeof width === 'number' ? width - 32 : 720)}
        destroyOnClose
      >
        {openToken && (
          <ApprovalDetail
            venueId={venueId}
            token={openToken}
            onDecided={handleDecided}
            onUnavailable={leaveFinishedHomeTask}
          />
        )}
      </Drawer>
    </Wrapper>
  )
}

export default Approvals
